import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { toastr } from 'react-redux-toastr';
import { Types } from 'mongoose';
import InputMask from 'react-input-mask';
import validator from 'validator';

import Select from '../../Select';

import { ItemsCreators } from '../../../store/actions/items';

import iconAdd from '../../../assets/images/icon_add_blue.svg';
import iconEdit from '../../../assets/images/icon_edit.svg';
import iconDelete from '../../../assets/images/icon_delete_custom.svg';
import { ReactComponent as IconBloco } from '../../../assets/images/bloco.svg';
import { ReactComponent as IconUrl } from '../../../assets/images/url.svg';
import { ReactComponent as IconPhone } from '../../../assets/images/telefone.svg';

const SelectItem = ({ value, onChange }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const items = useSelector((state) => state.items.list);

  const formattedOptions = useMemo(() => {
    if (items && items.length) {
      return items.map((item) => ({ label: item.title, value: item._id }));
    }
    return [];
  }, [items]);

  useEffect(() => {
    if (formattedOptions.length) {
      const findOption = formattedOptions.find(
        (option) => option.value === value
      );
      setSelectedOption(findOption);
      return;
    }

    setSelectedOption(null);
  }, [value, formattedOptions]);

  const handleChange = useCallback(
    (option) => {
      setSelectedOption(option);

      if (option) {
        onChange(option.value);
      } else {
        onChange('');
      }
    },
    [onChange]
  );

  return (
    <Select
      options={formattedOptions}
      value={selectedOption}
      onChange={handleChange}
      getOptionLabel={(option) => `${option.label}: ${option.value}`}
      isClearable
      className="pt-0 mb-2"
    />
  );
};

const ButtonSetting = ({ data, buttonKey, handleRemove, handleUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [buttonData, setButtonData] = useState({
    name: '',
    label: '',
    destination: {
      type: '',
      value: '',
    },
  });

  useEffect(() => {
    if (data) {
      setButtonData(data);
    }
  }, [data]);

  const handleExpand = useCallback(() => {
    setIsExpanded((oldState) => !oldState);
  }, []);

  const handleUpdateButtonData = useCallback(
    (updatedData) => {
      setButtonData(updatedData);
      handleUpdate(updatedData);
    },
    [handleUpdate]
  );

  const handleInputChange = useCallback(
    (event) => {
      const { value, name } = event.target;

      const updatedButton = { ...buttonData, [name]: value };
      handleUpdateButtonData(updatedButton);
    },
    [buttonData, handleUpdateButtonData]
  );

  const handleDestinationTypeChange = useCallback(
    (type) => {
      const updatedButton = {
        ...buttonData,
        destination: { type, value: '' },
      };

      handleUpdateButtonData(updatedButton);
    },
    [buttonData, handleUpdateButtonData]
  );

  const handleDestinationValueChange = useCallback(
    (event) => {
      const { value } = event.target;
      const updatedButton = {
        ...buttonData,
        destination: { ...buttonData.destination, value },
      };

      handleUpdateButtonData(updatedButton);
    },
    [buttonData, handleUpdateButtonData]
  );

  const phoneValidateOnBlur = (event) => {
    const { value } = event.target;
    const extractNumbers = value.replace(/\D/g, '');
    if (!extractNumbers) return;

    const isValidSize = extractNumbers.length === 11;
    if (isValidSize) return;

    toastr.error('Erro', 'Numero de telefone inválido');
    const updatedButton = {
      ...buttonData,
      destination: { ...buttonData.destination, value: '' },
    };

    handleUpdateButtonData(updatedButton);
  };

  const urlValidateOnBlur = (event) => {
    const { value } = event.target;
    if (!value) return;

    const isValidURL = validator.isURL(value, {
      protocols: ['http', 'https'],
      require_protocol: true,
    });

    if (isValidURL) return;

    toastr.error('Erro', 'Informe uma URL válida');
    const updatedButton = {
      ...buttonData,
      destination: { ...buttonData.destination, value: '' },
    };

    handleUpdateButtonData(updatedButton);
  };

  return (
    <>
      <div className="d-flex position-relative mt-2">
        <div className="carousel__button__name">
          {isEditingName ? (
            <input
              name="name"
              value={buttonData.name}
              onChange={handleInputChange}
              autoFocus
            />
          ) : (
            <span>{buttonData.name}</span>
          )}
        </div>
        <img
          src={iconEdit}
          alt="Editar"
          className="ml-2 pointer"
          onClick={() => setIsEditingName(!isEditingName)}
        />
        <img
          src={iconDelete}
          alt="Excluir"
          className="ml-2 pointer"
          onClick={handleRemove}
        />

        <img
          alt="Seta"
          className={classnames('seta-expander', 'pointer', {
            'arrow-rotation': !isExpanded,
          })}
          src="/assets/images/icons/seta_close.svg"
          onClick={handleExpand}
        />
      </div>

      <div hidden={!isExpanded}>
        <div className="common-form-group">
          <label>Título do botão (label)</label>
          <input
            placeholder="Título do botão"
            name="label"
            className="form-control mt-1"
            value={buttonData.label}
            onChange={handleInputChange}
          />
        </div>

        <p className="font-12 pt-2 pb-1">Destino</p>

        <div className="mb-1">
          <div
            className={classnames(
              'form-check',
              'form-check-inline',
              'form-check--light',
              { 'form-check--active': buttonData.destination.type === 'item' }
            )}
          >
            <input
              className="form-check-input"
              type="radio"
              id={`${buttonKey}_destination-item`}
              checked={buttonData.destination.type === 'item'}
              onChange={() => handleDestinationTypeChange('item')}
              hidden
            />
            <label
              className="form-check-label pl-1 pt-0 font-12 pointer d-flex align-items-center"
              htmlFor={`${buttonKey}_destination-item`}
            >
              <IconBloco />
              <span className="pl-1">Bloco</span>
            </label>
          </div>

          <div
            className={classnames(
              'form-check',
              'form-check-inline',
              'form-check--light',
              { 'form-check--active': buttonData.destination.type === 'url' }
            )}
          >
            <input
              className="form-check-input"
              type="radio"
              id={`${buttonKey}_destination-url`}
              checked={buttonData.destination.type === 'url'}
              onChange={() => handleDestinationTypeChange('url')}
              hidden
            />
            <label
              className="form-check-label pl-1 pt-0 font-12 pointer d-flex align-items-center"
              htmlFor={`${buttonKey}_destination-url`}
            >
              <IconUrl />
              <span className="pl-1">Url</span>
            </label>
          </div>

          <div
            className={classnames(
              'form-check',
              'form-check-inline',
              'form-check--light',
              { 'form-check--active': buttonData.destination.type === 'phone' }
            )}
          >
            <input
              className="form-check-input"
              type="radio"
              id={`${buttonKey}_destination-phone`}
              checked={buttonData.destination.type === 'phone'}
              onChange={() => handleDestinationTypeChange('phone')}
              hidden
            />
            <label
              className="form-check-label pl-1 pt-0 font-12 pointer d-flex align-items-center"
              htmlFor={`${buttonKey}_destination-phone`}
            >
              <IconPhone />
              <span className="pl-1">Telefone</span>
            </label>
          </div>
        </div>

        <div className="common-form-group">
          {buttonData.destination.type === 'item' && (
            <SelectItem
              value={buttonData.destination.value}
              onChange={(option) =>
                handleDestinationValueChange({ target: { value: option } })
              }
            />
          )}

          {buttonData.destination.type === 'url' && (
            <input
              placeholder="http://"
              className="form-control mt-1 mb-3"
              value={buttonData.destination.value}
              onChange={handleDestinationValueChange}
              onBlur={urlValidateOnBlur}
            />
          )}

          {buttonData.destination.type === 'phone' && (
            <InputMask
              type="tel"
              mask="(99) 99999-9999"
              maskChar=" "
              className="form-control mt-1 mb-3"
              placeholder="(00) 00000-0000"
              value={buttonData.destination.value || ''}
              onChange={handleDestinationValueChange}
              onBlur={phoneValidateOnBlur}
            />
          )}
        </div>
      </div>
    </>
  );
};

const CardSetting = ({
  card,
  handleRemove,
  handleUpdate,
  handleUpdateImage,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [cardData, setCardData] = useState({
    _id: '',
    name: '',
    imageUrl: '',
    title: '',
    description: '',
    buttons: [],
  });

  useEffect(() => {
    setCardData(card);
  }, [card]);

  const handleExpand = useCallback(() => {
    setIsExpanded((oldState) => !oldState);
  }, []);

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;

      const maximumLength = value.length > 50;

      if (maximumLength) {
        toastr.info('Ops', 'O máximo de caracteres aceito é 50');
        return;
      }

      const updatedCard = { ...card, [name]: value };

      setCardData(updatedCard);
      handleUpdate(updatedCard);
    },
    [card, handleUpdate]
  );

  const handleAddButton = useCallback(() => {
    const hasMaximumButtons = card.buttons.length === 4;

    if (hasMaximumButtons) {
      toastr.info('Ops', 'Não é possível adicionar mais de 4 botões');
      return;
    }

    const newButton = {
      name: `Botão ${card.buttons.length + 1}`,
      label: '',
      destination: { type: 'item', value: '' },
    };
    const updatedCard = { ...card, buttons: [...card.buttons, newButton] };

    setCardData(updatedCard);
    handleUpdate(updatedCard);
  }, [card, handleUpdate]);

  const handleRemoveButton = useCallback(
    (removeIndex) => {
      const hasMinimalButtons = card.buttons.length === 1;

      if (hasMinimalButtons) {
        toastr.info('Ops', 'Não é possível remover todos os botões');
        return;
      }

      const updatedButtons = card.buttons.filter(
        (_, _index) => _index !== removeIndex
      );
      const updatedCard = { ...card, buttons: updatedButtons };

      setCardData(updatedCard);
      handleUpdate(updatedCard);
    },
    [card, handleUpdate]
  );

  const handleUpdateButton = useCallback(
    (updatedButton, _index) => {
      const updatedButtons = card.buttons;
      updatedButtons[_index] = updatedButton;
      const updatedCard = { ...card, buttons: updatedButtons };

      setCardData(updatedCard);
      handleUpdate(updatedCard);
    },
    [card, handleUpdate]
  );

  const isValidFile = (file) => {
    const acceptedTypes = [
      'image/png',
      'image/jpg',
      'image/jpeg',
      'image/gif',
      'image/bmp',
    ];
    const hasAcceptedType = acceptedTypes.includes(file.type);

    if (!hasAcceptedType) {
      toastr.warning(
        'Erro de validação',
        'Tipo de arquivo não aceito no bloco de carrossel'
      );

      return false;
    }

    const MAX_IMAGE_SIZE = 1024 * 1024 * 15; // 15 MB
    const hasValidSize = file.size < MAX_IMAGE_SIZE;

    if (!hasValidSize) {
      toastr.warning('Ops', 'Por favor, a imagem deve ter no máximo 15 MB.');
      return false;
    }

    return true;
  };

  const handleChangeImage = useCallback(
    (event) => {
      const file = event.target?.files?.[0];

      if (file) {
        if (!isValidFile(file)) return;

        handleUpdateImage(file);
      }
    },
    [handleUpdateImage]
  );

  return (
    <div className="carousel__card">
      <div className="handler-title">
        <div className="d-flex">
          <div className="carousel__card__name">
            {isEditingName ? (
              <input
                name="name"
                value={cardData.name}
                onChange={handleInputChange}
                autoFocus
              />
            ) : (
              cardData.name
            )}
          </div>

          <img
            src={iconEdit}
            alt="Editar"
            className="ml-2 pointer"
            onClick={() => setIsEditingName(!isEditingName)}
          />
          <img
            src={iconDelete}
            alt="Excluir"
            className="ml-2 pointer"
            onClick={handleRemove}
            title="Deletar"
          />
        </div>

        <img
          alt="Seta"
          className={classnames('seta-expander', 'pointer', {
            'arrow-rotation': !isExpanded,
          })}
          src="/assets/images/icons/seta_close.svg"
          onClick={handleExpand}
        />
      </div>

      <div hidden={!isExpanded}>
        <div className="common-form-group">
          <label>Imagem</label>

          <div className="d-flex">
            <label
              className="btn btn-outline-primary btn--rounded py-0 pointer"
              htmlFor={`card-${cardData._id}-image-url`}
            >
              importar imagem
            </label>

            <input
              id={`card-${cardData._id}-image-url`}
              type="file"
              name="imageUrl"
              className="form-control pointer position-absolute fixed-top opacity-0"
              accept=".jpg,.jpeg,.png,.gif,.bmp"
              onChange={handleChangeImage}
              hidden
            />

            {cardData.imageUrl && (
              <span className="image-preview-container">
                <a
                  href={cardData.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Abrir imagem"
                >
                  <img src={cardData.imageUrl} alt="Imagem do card" />
                </a>
              </span>
            )}
          </div>
        </div>

        <div className="common-form-group">
          <label>Título do card</label>
          <input
            placeholder="Título do card"
            name="title"
            className="form-control mt-1"
            value={cardData.title}
            onChange={handleInputChange}
          />
        </div>

        <div className="common-form-group">
          <label>Descrição do card</label>
          <textarea
            placeholder="Descrição"
            name="description"
            className="form-control mt-1 height-70"
            value={cardData.description}
            onChange={handleInputChange}
          />
        </div>

        {cardData.buttons &&
          !!cardData.buttons.length &&
          cardData.buttons.map((button, index) => (
            <ButtonSetting
              key={`${index}-${cardData._id}`}
              buttonKey={`${index}-${cardData._id}`}
              index={index}
              data={button}
              handleRemove={() => handleRemoveButton(index)}
              handleUpdate={(updatedButton) =>
                handleUpdateButton(updatedButton, index)
              }
            />
          ))}

        <div className="font-12 mt-2">
          Novo botão
          <img
            src={iconAdd}
            alt="Adicionar"
            className="ml-2 pointer"
            onClick={handleAddButton}
          />
        </div>
      </div>
    </div>
  );
};

const CarouselSettings = ({ item, doUpdateItem }) => {
  const {
    carousel: { cards },
  } = item;

  const dispatch = useDispatch();

  const handleAddCard = useCallback(() => {
    const newCard = {
      _id: Types.ObjectId().toString(),
      name: `Card ${cards.length + 1}`,
      imageUrl: '',
      title: '',
      description: '',
      buttons: [
        {
          name: 'Botão 1',
          label: '',
          destination: { type: 'item', value: '' },
        },
      ],
    };

    const updatedItem = { ...item, carousel: { cards: [...cards, newCard] } };
    doUpdateItem(updatedItem);
  }, [cards, doUpdateItem, item]);

  const handleRemoveCard = useCallback(
    (removeIndex) => {
      const updatedCards = cards.filter((_, index) => index !== removeIndex);
      const updatedItem = { ...item, carousel: { cards: updatedCards } };

      doUpdateItem(updatedItem);
    },
    [cards, item, doUpdateItem]
  );

  const handleUpdateCard = useCallback(
    (index, updatedCard) => {
      const updatedCards = cards;
      updatedCards[index] = updatedCard;
      const updatedItem = { ...item, carousel: { cards: updatedCards } };

      doUpdateItem(updatedItem);
    },
    [cards, item, doUpdateItem]
  );

  const handleUpdateCardImage = useCallback(
    (cardId, file) => {
      dispatch(ItemsCreators.editCardImageRequest({ item, cardId, file }));
    },
    [dispatch, item]
  );

  return (
    <div className="carousel-settings">
      {!!cards.length &&
        cards.map((card, index) => (
          <CardSetting
            key={card._id}
            card={card}
            index={index}
            handleRemove={() => handleRemoveCard(index)}
            handleUpdate={(updatedCard) => handleUpdateCard(index, updatedCard)}
            handleUpdateImage={(file) => handleUpdateCardImage(card._id, file)}
          />
        ))}
      <div className="pt-3 pb-1 d-flex justify-content-between">
        Adicionar card
        <img
          src={iconAdd}
          alt="Adicionar"
          className="ml-2 pointer"
          onClick={handleAddCard}
        />
      </div>
    </div>
  );
};

export default CarouselSettings;
